import { z } from "zod";

/**
 * The vocabulary a Role deletion wizard speaks: which table a placement lives
 * in, what releasing it costs, and what a caller may decide about it.
 *
 * DECLARED HERE AND NOT BESIDE THE RESPONSE ENVELOPE, for the reason
 * `zroles.ts` gives for `RoleCapabilityCatalogViewSchema` in the opposite
 * direction: these are VOCABULARY — closed sets the backend switches on and the
 * dashboard renders from — while the envelope that carries them is transport.
 * The backend reaches this module through `@nexus/types`; it cannot reach
 * `api/domains/roles` at all, which is what makes the split load-bearing rather
 * than tidy.
 */

/**
 * Which of the FOUR placement tables a row lives in.
 *
 * A Role holds things through four tables, not one and not three:
 * `RoleResource` plus the three GRANT tables (`RoleCollectionGrant`,
 * `RoleWorkspaceGrant`, `RoleExternalToolGrant`). All four carry
 * `onDelete: Cascade` on the Role, so all four empty when it is deleted.
 *
 * `RoleGroupGrant` is deliberately NOT one of them — it grants a `UserGroup` one
 * app SURFACE on the Role, so it names no resource and there is nothing to
 * re-home. `RoleTask` is not one either: a task is the Role's own job
 * description, like `RoleWorkload` and `RoleScopeLine`, and names no resource
 * that could outlive the Role.
 *
 * This is the ADDRESS half of a decision, and it is deliberately the TABLE
 * rather than a resource kind, because the other half is that row's own primary
 * key. Addressing a `RoleResource` by `(resourceType, resourceId)` instead would
 * key on `@@unique([organizationId, resourceType, resourceId])` — exactly the
 * key another session is entitled to re-point, since `AssignResourcePort` IS the
 * move operation.
 */
export const ROLE_PLACEMENT_TABLES = [
  "resource",
  "collection_grant",
  "workspace_grant",
  "external_tool_grant"
] as const;

export const RolePlacementTableSchema = z.enum(ROLE_PLACEMENT_TABLES);

export type RolePlacementTable = z.infer<typeof RolePlacementTableSchema>;

/**
 * What RELEASING an element does to who can reach it.
 *
 * 🚨 SIX CLOSED STATES, BECAUSE THERE ARE SIX BEHAVIOURS. A single boolean, or a
 * blanket "releasing opens this to everyone", is wrong on four of them — and
 * wrong in both directions at once, which is what makes it worth a union. A
 * false warning on a tool grant teaches an owner to dismiss the banner; a
 * missing one on an Agent publishes a customer's resource with EDIT rights to
 * every member of the organisation.
 *
 * - `organization-gains-editor` — a `RoleResource` whose type maps to a
 *   `ResourceType` (agent, workflow, deployment, document_template) in an
 *   organisation whose resource visibility is OPEN.
 *   `RolePermissionSyncService.onResourceDetached` deletes the ROLE-subject
 *   `ResourcePermission` row and writes `ORGANIZATION -> EDITOR`. **EDITOR, not
 *   viewer** — everyone gains the right to CHANGE the thing.
 * - `organization-loses-access` — the same rows where visibility is CLOSED.
 *   `openWhenUngranted` is false, so no organisation row is written and the
 *   resource is left carrying none: org admins only. The SAME act, the opposite
 *   direction, decided by a setting invisible on the Role screen.
 * - `organization-gains-access-when-last-holder` — a Collection or Workspace
 *   grant where this Role is the LAST org-matched holder. Zero grants returns
 *   the resource to the set no Role has claimed, so callers placed in NO Role
 *   gain it and every Role-placed caller loses it; the revoke adapters move
 *   `accessScope` back to `ORGANIZATION` in the same transaction. ⚠️ THE MEMBER
 *   NAME IS WIDER THAN THE EFFECT — "the organization" here is its unplaced
 *   half, never everybody. Renaming it is a wire-contract change and is not one
 *   this comment may make quietly.
 * - `no-change-another-role-holds-it` — the same grant kinds where another Role
 *   still holds the element, so the narrowing survives.
 * - `no-change-not-permission-mapped` — `ai_task`.
 *   `ROLE_RESOURCE_TYPE_TO_RESOURCE_TYPE[AI_TASK]` is `null`, so the detach
 *   returns before writing anything.
 * - `no-change-not-access-controlled` — an external-tool grant. Only KNOWLEDGE
 *   and WORKSPACE are narrowable, and a catalogue tool carries no
 *   `ResourcePermission` mapping, so the grant is an attachment and nothing more.
 */
export const ROLE_RELEASE_EFFECTS = [
  "organization-gains-editor",
  "organization-loses-access",
  "organization-gains-access-when-last-holder",
  "no-change-another-role-holds-it",
  "no-change-not-permission-mapped",
  "no-change-not-access-controlled"
] as const;

export const RoleReleaseEffectSchema = z.enum(ROLE_RELEASE_EFFECTS);

export type RoleReleaseEffect = z.infer<typeof RoleReleaseEffectSchema>;

/**
 * The SECOND axis, which moves in the OPPOSITE direction to the first.
 *
 * An Agent or a Workflow is not only something people reach — it is something
 * that reaches. `RoleCallerResource` is an Agent id or a Workflow id, and the
 * RUNTIME narrowing family is an ALLOW-LIST: what a system may retrieve is what
 * the Roles holding it grant. So releasing one makes it an UNATTACHED, and in an
 * organisation with role enforcement on that holds at least one Role, an
 * unattached's confined-collection set is EMPTY — it retrieves nothing.
 *
 * That restriction lands at the same instant as the widening above, on the same
 * element, and neither implies the other. Reporting only the access axis tells
 * an owner their agent was shared while its retrieval silently went dark.
 */
export const ROLE_RUNTIME_REACH_EFFECTS = ["becomes-orphan", "not-applicable"] as const;

export const RoleRuntimeReachEffectSchema = z.enum(ROLE_RUNTIME_REACH_EFFECTS);

export type RoleRuntimeReachEffect = z.infer<typeof RoleRuntimeReachEffectSchema>;

/**
 * What a caller has decided to do with one element.
 *
 * `RELEASE` rather than `DELETE`, and the word is not cosmetic: removing a
 * placement removes a RESTRICTION at least as often as it removes access, so a
 * control labelled `delete` would state the opposite of what it does on half the
 * element kinds. What a release actually costs is
 * {@link RoleReleaseEffectSchema}, computed per element rather than asserted
 * once.
 */
export const ROLE_DELETION_ACTIONS = ["REASSIGN", "RELEASE"] as const;

export const RoleDeletionActionSchema = z.enum(ROLE_DELETION_ACTIONS);

export type RoleDeletionAction = z.infer<typeof RoleDeletionActionSchema>;
